import BackgroundImage from "@components/BackgroundImageCst";
import { Box, Container, useTheme, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import Breadcrumb from "@components/Breadcrumb";
import HeroCst from "@components/HeroCst";
import AnimationFadeIn from "@components/AnimationFadeIn";
import { ServicesURL } from "@utils/utilsApi";
import ButtonBackPage from "@components/ButtonBackPage";
import { useParams } from "react-router-dom";
import { SelectCst } from "@components/InputCst";
import { useEffect, useState, useRef } from "react";
import { useAuth } from "@context/auth";
import { RolesDigitedEnum } from "@context/auth/types";
import useSWRMutation from "swr/mutation";
import { fetcher } from "src/services/config";
import { MethodType } from "src/services/type";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";

/* ================= TYPES ================= */

interface DashboardType {
  id: string; // quicksight id
  idDashboard: string; // id db
  quicksightUrl: string;
  metabaseQuestionId: string | null;
  openLearningPlatformName?: string;
  originalName?: string;
}

interface SelectOptions {
  value: string;
  label: string;
}

const DashboardVisualization: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation(["dashboard-visualization-page", "common"]);
  const { id = "1" } = useParams();
  const { user } = useAuth();

  const [dashboards, setDashboards] = useState<DashboardType[]>([]);
  const [options, setOptions] = useState<SelectOptions[]>([]);
  const [selectedDashboardId, setSelectedDashboardId] = useState("");
  const [urlIframe, setUrlIframe] = useState("");
  const [isFullscreen, setIsFullscreen] = useState(false);

  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  const isPO = user?.roles.includes(RolesDigitedEnum.PRODUCT_OWNER);

  const { trigger: triggerDashboardDetail } = useSWRMutation(
    `${ServicesURL.getDashboard}?getUsersAndRoles=true&corporateId=${id}${isPO ? "" : `&userId=${user?.id_user}`}`,
    (url: string) => {
      return fetcher<any>(url, MethodType.GET);
    },
  );

  const getAllDashboards = async () => {
    try {
      let data = await triggerDashboardDetail();

      const dashboardsList: DashboardType[] = data?.output || [];

      setDashboards(dashboardsList);

      const optionsCalculated: SelectOptions[] = dashboardsList.map((opt) => ({
        value: opt.idDashboard,
        label: opt.openLearningPlatformName || opt.originalName || "",
      }));

      setOptions(optionsCalculated);

      // AUTO LOAD
      if (dashboardsList.length > 0) {
        loadDashboard(dashboardsList[0]);
      }
    } catch (error) {
      console.error("Error loading dashboards", error);
    }
  };

  const loadDashboard = async (dashboard: DashboardType) => {
    try {
      setSelectedDashboardId(dashboard.idDashboard);

      if (dashboard.metabaseQuestionId && !dashboard.quicksightUrl) {
        let data = await fetcher(
          `${ServicesURL.getMetabaseUrl}?idDashboard=${dashboard.idDashboard}&idUser=${user?.id_user}`,
          MethodType.GET,
        );

        setUrlIframe(data?.output || "");
      } else {
        // QUICKSIGHT
        setUrlIframe(dashboard.quicksightUrl || "");
      }
    } catch (error) {
      console.error("Error loading dashboard", error);
    }
  };

  const handleDashboardChange = (dashboardId: string) => {
    const dashboard = dashboards.find((d) => d.idDashboard === dashboardId);

    if (!dashboard) return;

    loadDashboard(dashboard);
  };

  useEffect(() => {
    getAllDashboards();
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  const toggleFullscreen = () => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    if (!iframe.requestFullscreen) {
      alert("Fullscreen non supportato dal browser");
      return;
    }

    if (!document.fullscreenElement) {
      iframe
        .requestFullscreen()
        .catch((err) => console.error("Errore fullscreen:", err));
    } else {
      document
        .exitFullscreen()
        .catch((err) => console.error("Errore uscita fullscreen:", err));
    }
  };

  return (
    <AnimationFadeIn>
      <BackgroundImage
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-dashboard.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container
        maxWidth={"xxl"}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <HeroCst title={t("hero-title")} description={t("hero-description")} />
        <Box
          sx={{
            [theme.breakpoints.up("md")]: {
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            },
          }}
        >
          <ButtonBackPage />

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <SelectCst
              id={"dashboard"}
              label={"Dashboard"}
              value={selectedDashboardId}
              options={options}
              onChange={(value: any) => handleDashboardChange(value)}
              sx={{
                width: "100%",
                background: theme.palette.background.paper,
                [theme.breakpoints.up("md")]: {
                  maxWidth: "400px",
                },
              }}
            />

            <Tooltip title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}>
              <IconButton onClick={toggleFullscreen}>
                {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Box
          sx={{
            mt: "25px",
            "& iframe": {
              border: 0,
            },
          }}
        >
          <iframe
            ref={iframeRef}
            src={urlIframe}
            width={"100%"}
            height={"700px"}
            title="iframe"
          ></iframe>
        </Box>
      </Container>
    </AnimationFadeIn>
  );
};

export default DashboardVisualization;
